import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, MoreVertical, MoreHorizontal, Package, Info } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from './LoadingSpinner';

export type StammdatenSidebarFolder = {
  id: string;
  name: string;
  itemCount: number;
};

export type StammdatenSidebarSupplier = {
  id: string;
  name: string;
  itemCount: number;
  logoUrl: string | null;
};

type StammdatenOrdnerSidebarProps = {
  activeTab: 'materialien' | 'leistungen' | 'vorlagen';
  onTabChange: (tab: 'materialien' | 'leistungen' | 'vorlagen') => void;
  /** LV-Vorlagen hängen am Recht web.vorlagen.view (Modul „Vorlagen“). */
  showVorlagenTab?: boolean;
  /** Nur „Ordner“ (Materialien: material-Ordner; Leistungen: Leistungs-Ordner) */
  ordnerFolders: StammdatenSidebarFolder[];
  /**
   * Großhändler-Kataloge (geteilter Material-Katalog pro Großhändler).
   * Nur bei Materialien anzeigen.
   */
  supplierCatalogs?: StammdatenSidebarSupplier[];
  showSupplierCatalogsSection: boolean;
  /**
   * Aktuell ausgewählter Großhändler (Filter auf supplier_id im Listing).
   * `null` = kein Großhändler-Filter aktiv.
   */
  selectedSupplierId?: string | null;
  onSelectSupplier?: (supplierId: string | null) => void;
  selectedFolderId: string | null;
  onSelectFolder: (id: string | null) => void;
  allItemsLabel: string;
  allItemsCount: number;
  onAddOrdner: () => void;
  folderMenuOpenId: string | null;
  onToggleFolderMenu: (folderId: string) => void;
  onRequestDeleteFolder: (folder: StammdatenSidebarFolder) => void;
  /** Toggle Großhändler ein-/ausblenden (web.stammdaten.edit). */
  canManageSupplierCatalogs?: boolean;
  /** Nach Speichern der Aktivierung Sidebar neu laden. */
  onSupplierCatalogsChanged?: () => void;
};

/** Zähler-Spalte und Aktions-Slot müssen in allen Zeilentypen gleich breit sein, damit die Zahlen fluchten. */
const ROW_COUNT_CLASS = 'w-14 shrink-0 text-right text-xs tabular-nums';
const ROW_ACTION_SLOT_CLASS = 'flex w-7 shrink-0 items-center justify-center';

function formatCountCompact(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0';
  if (value < 1000) return String(Math.trunc(value));
  return new Intl.NumberFormat('de-DE', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function FolderRow({
  folder,
  selected,
  onSelect,
  menuOpen,
  onToggleMenu,
  onDelete,
}: {
  folder: StammdatenSidebarFolder;
  selected: boolean;
  onSelect: () => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={`group flex items-stretch min-w-0 rounded-md text-sm ${
        selected ? 'bg-app-surface-muted' : 'hover:bg-app-hover'
      }`}
    >
      <button
        type="button"
        className="flex flex-1 min-w-0 items-center gap-1 rounded-md px-2 py-2 border-0 bg-transparent font-inherit text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
        onClick={onSelect}
      >
        <span className="flex-1 min-w-0 truncate text-app-text">{folder.name}</span>
        <span className={`${ROW_COUNT_CLASS} text-app-text-subtle`}>
          {formatCountCompact(folder.itemCount)}
        </span>
      </button>
      <div className={`relative ${ROW_ACTION_SLOT_CLASS}`}>
        <button
          type="button"
          className="p-1 rounded text-app-text-subtle hover:text-app-text-muted hover:bg-app-hover opacity-70 group-hover:opacity-100"
          aria-label="Ordner-Aktionen"
          onClick={(e) => {
            e.stopPropagation();
            onToggleMenu();
          }}
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {menuOpen && (
          <div
            className="absolute right-0 top-full mt-0.5 z-30 min-w-[140px] rounded-lg border border-app-border bg-app-surface py-1 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50"
              onClick={onDelete}
            >
              Ordner löschen…
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SupplierRow({
  supplier,
  selected,
  onSelect,
}: {
  supplier: StammdatenSidebarSupplier;
  selected: boolean;
  onSelect: () => void;
}) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const showLogo = supplier.logoUrl && !imgFailed;
  return (
    <button
      type="button"
      className={`group flex w-full items-center gap-2 min-w-0 rounded-md px-2 py-2 text-sm cursor-pointer bg-transparent font-inherit text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
        selected ? 'bg-pipedrive-blue/8' : 'hover:bg-app-hover'
      }`}
      onClick={onSelect}
    >
      <span
        className={`shrink-0 flex h-5 w-5 items-center justify-center overflow-hidden rounded transition-colors ${
          selected ? 'bg-pipedrive-blue/10 text-pipedrive-blue' : 'bg-app-surface-muted text-app-text-subtle'
        }`}
      >
        {showLogo ? (
          <img
            src={supplier.logoUrl ?? ''}
            alt=""
            className="w-5 h-5 object-contain"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <Package className="w-3.5 h-3.5" />
        )}
      </span>
      <span className={`flex-1 min-w-0 truncate ${selected ? 'font-medium text-pipedrive-blue' : 'text-app-text'}`}>
        {supplier.name}
      </span>
      <span className="flex shrink-0 items-center">
        <span
          className={`${ROW_COUNT_CLASS} ${selected ? 'text-pipedrive-blue' : 'text-app-text-subtle'}`}
        >
          {formatCountCompact(supplier.itemCount)}
        </span>
        <span className={ROW_ACTION_SLOT_CLASS} aria-hidden />
      </span>
    </button>
  );
}

type ActivationItem = {
  id: string;
  name: string;
  enabled: boolean;
};

function SupplierActivationPanel({
  open,
  onClose,
  triggerRef,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onSaved: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const [items, setItems] = useState<ActivationItem[]>([]);
  const [draft, setDraft] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch('/api/settings/suppliers', { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error((data as { error?: string })?.error || 'Laden fehlgeschlagen.');
        const list = Array.isArray((data as { items?: ActivationItem[] })?.items)
          ? (data as { items: ActivationItem[] }).items
          : [];
        if (cancelled) return;
        setItems(list.map((s) => ({ id: String(s.id), name: String(s.name), enabled: Boolean(s.enabled) })));
        const next: Record<string, boolean> = {};
        for (const s of list) next[String(s.id)] = Boolean(s.enabled);
        setDraft(next);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const width = 280;
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      setPanelPos({ top: rect.bottom + 6, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, triggerRef]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = items.map((s) => ({
        supplier_id: s.id,
        enabled: Boolean(draft[s.id]),
      }));
      const res = await fetch('/api/settings/suppliers', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items: payload }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data as { error?: string })?.error || 'Speichern fehlgeschlagen.');
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !panelPos || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9999] w-[280px] rounded-lg border border-app-border bg-app-surface shadow-lg"
      style={{ top: panelPos.top, left: panelPos.left }}
      role="dialog"
      aria-label="Großhändler-Kataloge ein- oder ausblenden"
    >
      <div className="border-b border-app-border px-3 py-2">
        <p className="text-sm font-medium text-app-text">Kataloge anzeigen</p>
        <p className="text-xs text-app-text-subtle mt-0.5">Ein- und ausgeblendete Großhändler für diese Instanz.</p>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {loading ? (
          <InlineLoadingState label="Laden…" className="px-3 py-6" />
        ) : items.length === 0 ? (
          <p className="px-3 py-4 text-xs text-app-text-subtle">Keine Großhändler vorhanden.</p>
        ) : (
          items.map((s) => (
            <label
              key={s.id}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-2 hover:bg-app-hover"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                checked={Boolean(draft[s.id])}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDraft((prev) => ({ ...prev, [s.id]: checked }));
                }}
              />
              <span className="min-w-0 flex-1 truncate text-sm text-app-text">{s.name}</span>
            </label>
          ))
        )}
      </div>
      {error && <p className="px-3 pb-2 text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2 border-t border-app-border px-3 py-2">
        <button
          type="button"
          className="rounded-md px-2.5 py-1.5 text-sm text-app-text-muted hover:bg-app-hover"
          onClick={onClose}
          disabled={saving}
        >
          Abbrechen
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          onClick={() => void save()}
          disabled={saving || loading}
        >
          {saving ? <LoadingSpinner size="sm" /> : null}
          Speichern
        </button>
      </div>
    </div>,
    document.body
  );
}

export const StammdatenOrdnerSidebar: React.FC<StammdatenOrdnerSidebarProps> = ({
  activeTab,
  onTabChange,
  showVorlagenTab = true,
  ordnerFolders,
  supplierCatalogs = [],
  showSupplierCatalogsSection,
  selectedSupplierId = null,
  onSelectSupplier,
  selectedFolderId,
  onSelectFolder,
  allItemsLabel,
  allItemsCount,
  onAddOrdner,
  folderMenuOpenId,
  onToggleFolderMenu,
  onRequestDeleteFolder,
  canManageSupplierCatalogs = false,
  onSupplierCatalogsChanged,
}) => {
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const manageButtonRef = useRef<HTMLButtonElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [manageOpen, setManageOpen] = useState(false);

  const showTooltip = () => {
    const rect = infoButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({ top: rect.bottom + 6, left: rect.left });
    }
    setTooltipVisible(true);
  };

  const hideTooltip = () => setTooltipVisible(false);

  return (
    <aside className="hidden md:flex w-[272px] shrink-0 flex-col min-h-0 overflow-hidden border-r border-app-border bg-app-surface">
      <div className="flex border-b border-app-border px-1 pt-2 shrink-0">
        <button
          type="button"
          onClick={() => onTabChange('materialien')}
          className={`flex-1 pb-2.5 text-sm font-medium transition-colors ${
            activeTab === 'materialien'
              ? 'border-pipedrive-blue text-pipedrive-blue border-b-2 -mb-px'
              : 'border-transparent text-app-text-subtle border-b-2 -mb-px hover:text-app-text'
          }`}
        >
          Materialien
        </button>
        <button
          type="button"
          onClick={() => onTabChange('leistungen')}
          className={`flex-1 pb-2.5 text-sm font-medium transition-colors ${
            activeTab === 'leistungen'
              ? 'border-pipedrive-blue text-pipedrive-blue border-b-2 -mb-px'
              : 'border-transparent text-app-text-subtle border-b-2 -mb-px hover:text-app-text'
          }`}
        >
          Leistungen
        </button>
        {showVorlagenTab ? (
        <button
          type="button"
          onClick={() => onTabChange('vorlagen')}
          className={`flex-1 pb-2.5 text-sm font-medium transition-colors ${
            activeTab === 'vorlagen'
              ? 'border-pipedrive-blue text-pipedrive-blue border-b-2 -mb-px'
              : 'border-transparent text-app-text-subtle border-b-2 -mb-px hover:text-app-text'
          }`}
        >
          Vorlagen
        </button>
        ) : null}
      </div>

      <div className="app-scrollbar flex-1 overflow-y-auto px-3 py-4 space-y-6">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-app-text-muted">Ordner</span>
            <button
              type="button"
              onClick={onAddOrdner}
              className="p-1 rounded text-app-text-subtle hover:text-app-text hover:bg-app-hover"
              aria-label="Ordner hinzufügen"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-0.5">
            <button
              type="button"
              className={`flex w-full items-center gap-1 min-w-0 rounded-md px-2 py-2 text-sm cursor-pointer border-0 bg-transparent font-inherit text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${
                selectedFolderId === null && !selectedSupplierId ? 'bg-app-surface-muted' : 'hover:bg-app-hover'
              }`}
              onClick={() => {
                onSelectFolder(null);
                onSelectSupplier?.(null);
              }}
            >
              <span className="flex-1 min-w-0 truncate text-app-text">{allItemsLabel}</span>
              <span className="flex shrink-0 items-center">
                <span className={`${ROW_COUNT_CLASS} text-app-text-subtle`}>{formatCountCompact(allItemsCount)}</span>
                <span className={ROW_ACTION_SLOT_CLASS} aria-hidden />
              </span>
            </button>
            {ordnerFolders.map((f) => {
              const fid = String(f.id);
              return (
                <React.Fragment key={fid}>
                  <FolderRow
                    folder={{ ...f, id: fid }}
                    selected={String(selectedFolderId ?? '') === fid}
                    onSelect={() => {
                      onSelectFolder(fid);
                      onSelectSupplier?.(null);
                    }}
                    menuOpen={String(folderMenuOpenId ?? '') === fid}
                    onToggleMenu={() => onToggleFolderMenu(fid)}
                    onDelete={() => onRequestDeleteFolder({ ...f, id: fid })}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {showSupplierCatalogsSection && (
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-medium text-app-text-muted">Großhändler Kataloge</span>
                <button
                  ref={infoButtonRef}
                  type="button"
                  className="p-0.5 rounded text-app-text-subtle hover:text-app-text-muted"
                  aria-label="Information zu Großhändler-Katalogen"
                  onMouseEnter={showTooltip}
                  onMouseLeave={hideTooltip}
                  onFocus={showTooltip}
                  onBlur={hideTooltip}
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
                {tooltipVisible && (
                  <div
                    className="fixed z-[9999] w-56 rounded-md border border-app-border bg-app-surface px-2.5 py-2 text-xs leading-relaxed text-app-text-muted shadow-lg"
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                  >
                    Du möchtest weitere Großhändler hinzufügen? Dann kontaktiere uns unter{" "}
                    <a
                      href="mailto:dennis@meisterpfad.de"
                      className="text-app-text-muted underline break-all"
                    >
                      dennis@meisterpfad.de
                    </a>
                    .
                  </div>
                )}
              </div>
              {canManageSupplierCatalogs ? (
                <button
                  ref={manageButtonRef}
                  type="button"
                  className="p-1 rounded text-app-text-subtle hover:text-app-text hover:bg-app-hover"
                  aria-label="Großhändler-Kataloge verwalten"
                  aria-expanded={manageOpen}
                  onClick={() => setManageOpen((v) => !v)}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            <SupplierActivationPanel
              open={manageOpen}
              onClose={() => setManageOpen(false)}
              triggerRef={manageButtonRef}
              onSaved={() => onSupplierCatalogsChanged?.()}
            />
            <div className="space-y-0.5">
              {supplierCatalogs.length === 0 ? (
                <p className="text-xs text-app-text-subtle px-2 py-1">Noch keine Kataloge.</p>
              ) : (
                supplierCatalogs.map((s) => (
                  <SupplierRow
                    key={s.id}
                    supplier={s}
                    selected={selectedSupplierId === s.id}
                    onSelect={() => {
                      onSelectSupplier?.(s.id);
                    }}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};
